import React, { useState, useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";
import { NavLink } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { CardActionArea } from "@mui/material";
import englishImage from "../../assets/Images/EnglishCards/English1.png";
import englishImageSecond from "../../assets/Images/EnglishCards/English2.webp";
import englishImageThird from "../../assets/Images/EnglishCards/English3.jpeg";
import englishImageFourth from "../../assets/Images/EnglishCards/English4.jpeg";
import french from "../../assets/Images/EnglishCards/FrenchFlag.jpeg";
import scaCourse from "../../assets/Images/EnglishCards/EnglishBanner.webp";
import "./HomePageCards.css";

const HomePageCards = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const firstFormNodeRef = useRef(null);
  const secondFormNodeRef = useRef(null);
  const thirdFormNodeRef = useRef(null);
  const fourthFormNodeRef = useRef(null);
  const frenchNodeRef = useRef(null);

  useEffect(() => {
    const images = [
      englishImage,
      englishImageSecond,
      englishImageThird,
      englishImageFourth,
      french,
    ];
    const promises = images.map((img) => {
      return new Promise((resolve) => {
        const image = new Image();
        image.src = img;
        image.onload = resolve;
      });
    });
    Promise.all(promises).then(() => {
      setIsLoaded(true);
    });
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Banner at the top */}
      <div className="englishBannerContainer">
        <img src={scaCourse} alt="scaCourseBanner" className="englishBanner" />
      </div>

      {/* Original styling for the card container */}
      <div className="englishCoursesList">
        <CSSTransition
          nodeRef={firstFormNodeRef}
          unmountOnExit
          key={0}
          in={isLoaded}
          appear={true}
          timeout={300}
          classNames="card"
        >
          <Card
            ref={firstFormNodeRef}
            style={{
              width: "45%",
              transition: "transform 0.3s ease-in-out",
              marginTop: "32px",
            }}
          >
            <NavLink to="/FirstFormPageDetails" end>
              <CardActionArea>
                <CardMedia
                  component="img"
                  height="200"
                  image={englishImage}
                  alt="First Form English"
                />
                <CardContent className="firstFormTextContainer">
                  <Typography
                    className="formLevelName"
                    color="common.white"
                    gutterBottom
                    variant="h5"
                    component="div"
                  >
                    First Form
                  </Typography>
                  <Typography
                    className="formLevelDetails"
                    variant="body2"
                    color="common.white"
                  >
                    This integrated course is designed for students in their
                    first year of secondary school. It seeks to equip students
                    with skills to express themselves using grammatically
                    correct English. Students will use their reading, writing,
                    listening, and speaking skills as they develop and apply
                    effective study skills. It offers opportunities for students
                    to communicate facts and ideas orally. Students will examine
                    the craft of short stories, poetry, novel and drama. They
                    will learn approaches to analysis and interpretation through
                    reading, examining, analyzing, and writing about short
                    stories, poetry, novel and drama.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </NavLink>
          </Card>
        </CSSTransition>

        <CSSTransition
          nodeRef={secondFormNodeRef}
          unmountOnExit
          key={1}
          in={isLoaded}
          appear={true}
          timeout={300}
          classNames="card"
        >
          <Card
            ref={secondFormNodeRef}
            style={{
              width: "45%",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <NavLink to="/SecondFormPageDetails" end>
              <CardActionArea>
                <CardMedia
                  component="img"
                  height="200"
                  image={englishImageSecond}
                  alt="Second Form English"
                />
                <CardContent className="secondFormTextContainer">
                  <Typography className="formLevelName" gutterBottom variant="h5" component="div">
                    Second Form
                  </Typography>
                  <Typography className="formLevelDetails" variant="body2">
                    This course is designed to integrate the grammatical rules
                    learnt in first form with the different types of writing as
                    well as the major forms of literature such as novels, short
                    stories, plays, poems, and reading comprehension passages to
                    develop an understanding and appreciation of literary
                    language. Students will be engaging in a new and innovative
                    way of appreciating and learning grammar and literature and
                    all its components: literary elements, writing styles,
                    vocabulary, literary analysis, and discussions. Students
                    will further develop their reading skills, language skills,
                    expressive skills, and critical thinking skills.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </NavLink>
          </Card>
        </CSSTransition>

        <CSSTransition
          nodeRef={thirdFormNodeRef}
          unmountOnExit
          key={2}
          in={isLoaded}
          appear={true}
          timeout={300}
          classNames="card"
        >
          <Card
            ref={thirdFormNodeRef}
            style={{
              width: "45%",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <NavLink to="/ThirdFormPageDetails" end>
              <CardActionArea>
                <CardMedia
                  component="img"
                  height="200"
                  image={englishImageThird}
                  alt="Third Form English"
                />
                <CardContent className="thirdFormTextContainer">
                  <Typography
                    className="formLevelName"
                    gutterBottom
                    variant="h5"
                    color="common.white"
                    component="div"
                  >
                    Third Form
                  </Typography>
                  <Typography
                    className="formLevelDetails"
                    variant="body2"
                    color="common.white"
                  >
                    The course is designed to integrate the grammatical rules
                    learnt in the lower forms with different types of writing.
                    Students will practice factual and reflective writing in
                    line with the requirements of the SBA component for English
                    A and B. The course will reinforce and develop further
                    critical thinking skills, analytical skills, and sharpen
                    writing skills. Comprehensions will be assigned to students
                    to improve their reading skills and vocabulary will be
                    developed to improve their expressive skills. Students will
                    begin to complete the School Based Assessment for CSEC
                    English A and B which will be completed in their fourth
                    year.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </NavLink>
          </Card>
        </CSSTransition>

        <CSSTransition
          nodeRef={fourthFormNodeRef}
          unmountOnExit
          key={3}
          in={isLoaded}
          appear={true}
          timeout={300}
          classNames="card"
        >
          <Card
            ref={fourthFormNodeRef}
            style={{
              width: "45%",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <NavLink to="/FourthFormPageDetails" end>
              <CardActionArea>
                <CardMedia
                  component="img"
                  height="200"
                  image={englishImageFourth}
                  alt="Fourth Form English"
                />
                <CardContent className="fourthFormTextContainer">
                  <Typography
                    className="formLevelName"
                    gutterBottom
                    variant="h5"
                    color="common.white"
                    component="div"
                  >
                    Fourth Form
                  </Typography>
                  <Typography
                    className="formLevelDetails"
                    variant="body2"
                    color="common.white"
                  >
                    In this course students will learn to write persuasive
                    essays. They will refine their writing style by reviewing
                    appropriate use of paragraph hooks, passive voice, parallel
                    structure, and literary devices. Students will practice
                    factual and descriptive writing techniques and will be given
                    opportunity to study and write short stories. Students will
                    complete the School Based Assessment for English A and B
                    (SBA) by writing three reflections and crafting an oral
                    presentation. The reflective writing will shape their
                    thinking about each topic, it will allow them to analyze the
                    language in each artifact, and it will display their
                    personal growth.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </NavLink>
          </Card>
        </CSSTransition>

        <CSSTransition
          nodeRef={frenchNodeRef}
          unmountOnExit
          key={4}
          in={isLoaded}
          appear={true}
          timeout={300}
          classNames="card"
        >
          <Card
            ref={frenchNodeRef}
            style={{
              width: "45%",
              transition: "transform 0.3s ease-in-out",
              marginTop: "30px",
            }}
          >
            <NavLink to="/FrenchPageDetails" end>
              <CardActionArea>
                <CardMedia
                  component="img"
                  height="200"
                  image={french}
                  alt="French"
                />
                <CardContent className="frenchTextContainer">
                  <Typography
                    className="formLevelName"
                    gutterBottom
                    variant="h5"
                    color="#333"
                    component="div"
                  >
                    French Class
                  </Typography>
                  <Typography className="formLevelDetails" variant="body2" color="#333">
                    The course is for students with no knowledge of French.
                    Classes use a communicative approach and the emphasis is on
                    oral proficiency, comprehension, and expression while
                    exploring cultural context. Reading and writing skills are
                    also developed. Through extensive practice in class,
                    students acquire the basic vocabulary and constructions of
                    French, providing a sound basis for further studies.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </NavLink>
          </Card>
        </CSSTransition>
      </div>
    </div>
  );
};

export default HomePageCards;
